/**
 * Desktop-only feature gates — guidance, not error (ENG-4332, plan G-web-guidance).
 *
 * Some Studio capabilities can only work in the desktop app: they spawn local
 * processes, bind local ports, or talk to a bridge the browser cannot host. When a
 * browser user reaches one, that is a NORMAL, expected platform limitation. Recording
 * it as a thrown error turns it into a PostHog `$exception`, which pollutes error
 * groups and makes an ordinary product boundary look like a defect.
 *
 * This module is the single place that decides "is this surface allowed to run a
 * desktop-only feature?" and the single place that emits the guidance signal. Gate
 * call sites ask it instead of re-deriving `isElectronApp()` and throwing.
 *
 * Per rule: think-in-systems (ai.knowledge/global) — one derivation point for the
 * surface decision and one emitter, so a new gate is two lines at the call site.
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */

import { isElectronApp } from '#~/electron/utils.js';
import { track } from '#~/analytics/track.js';
import { EVENT_CATEGORIES } from '../constants/event-categories.js';
import { EVENT_NAMES } from '../constants/event-names.js';
import {
  COMMON_PROPERTIES,
  ENVELOPE_CLIENT,
  EVENT_SEVERITY,
  FEATURE_GATE_PROPERTIES,
  FEATURE_GATE_REASON,
  type DesktopOnlyFeature,
  type EnvelopeClient
} from '../constants/event-properties.js';

/**
 * Which client surface this renderer is. Reads the SAME `isElectronApp()` flag the
 * analytics envelope resolver reads (analytics/utils/context-resolver.ts), so the
 * `client` a gate reports can never disagree with the `client` on the envelope.
 */
export const resolveFeatureGateClient = (): EnvelopeClient =>
  isElectronApp() ? ENVELOPE_CLIENT.ELECTRON : ENVELOPE_CLIENT.STANDALONE_WEB;

/**
 * True when this surface can actually run desktop-only features. Callers gate on this
 * rather than on `isElectronApp()` directly so the surface decision has one owner.
 */
export const isDesktopOnlyFeatureAvailable = (
  client: EnvelopeClient = resolveFeatureGateClient()
): boolean => client === ENVELOPE_CLIENT.ELECTRON;

/**
 * Record that a desktop-only feature was reached from a surface that cannot run it.
 *
 * Emits exactly one canonical `feature_gate_guidance_shown` per call, with no internal
 * batching or dedupe.
 *
 * CALLER CONTRACT — the count is only meaningful if every call site is reachable ONLY by
 * a user gesture. This event feeds a *demand* ranking ("what are browser users trying to
 * do"), so calling it from the app's own machinery — a connect loop, a reconnect handler,
 * a retry watchdog — makes the count measure attempts instead of people, and a single
 * automatic call site can outweigh every real one by orders of magnitude. There is no way
 * to detect that from inside this function, because "was a human responsible" is a fact
 * about the call graph; the call sites are therefore declared and enforced in
 * `feature-gate.call-sites.test.ts`. Add a gate there when you add one here.
 *
 * On the desktop surface this is a no-op and emits NOTHING: desktop behaviour must be
 * byte-identical to before this module existed.
 *
 * `client` is an explicit parameter defaulting to the resolver so a gate that already
 * resolved the surface passes its value through instead of resolving twice (and so the
 * value stamped on the envelope is provably the value the gate branched on).
 *
 * @returns true when the guidance signal was emitted (i.e. the gate was closed).
 */
export const reportDesktopOnlyFeatureGate = (
  feature: DesktopOnlyFeature,
  client: EnvelopeClient = resolveFeatureGateClient()
): boolean => {
  if (isDesktopOnlyFeatureAvailable(client)) {
    return false;
  }

  // Fire-and-forget: track() never rejects (it swallows provider errors internally), and
  // a gate must never make the caller await telemetry before answering the user.
  void track({
    name: EVENT_NAMES.FEATURE_GATE_GUIDANCE_SHOWN,
    category: EVENT_CATEGORIES.APP_LIFECYCLE,
    properties: {
      [FEATURE_GATE_PROPERTIES.FEATURE]: feature,
      [FEATURE_GATE_PROPERTIES.GATE_REASON]: FEATURE_GATE_REASON.DESKTOP_ONLY,
      // The surface the gate branched on. NOT `client`: PostHogManager re-derives
      // `client` from the running process and spreads it last over every capture
      // (manager.ts getClientProperties), so stamping it here would be silently
      // discarded. See FEATURE_GATE_PROPERTIES.GATE_CLIENT.
      [FEATURE_GATE_PROPERTIES.GATE_CLIENT]: client,
      // S4/info tells alerting this is never an incident. Without it, a guidance spike
      // reads like an error spike — the exact confusion this event exists to end.
      [COMMON_PROPERTIES.SEVERITY]: EVENT_SEVERITY.S4_INFO
    }
  });

  return true;
};
